import React, { useMemo, useState } from "react";
import { StyledTitle } from "../../styles";
import {
  FundingContainer,
  FundingContent,
  Funder,
  DateRange,
  Amount,
  Title,
  PIs,
  PILabel,
  FundingAgencyDescription,
} from "./styles";
import { Stack, Row, Col, Button } from "react-bootstrap";
import Authors from "../../components/Authors";
import { Switch, Route, useRouteMatch } from "react-router-dom";
import { Helmet } from "react-helmet";

const Grant = ({ grant, people }) => {
  return (
    <div>
      <Row className="align-items-baseline">
        <Col className="mb-3">
          <Funder>{grant.funder}</Funder>
        </Col>
        <Col md="auto" className="mb-3">
          <Amount>
            {grant.amount}
            {grant.amount_we_get && <> ({grant.amount_we_get})</>}
          </Amount>
        </Col>
      </Row>

      <Row className="align-items-baseline">
        <Col className="mb-3">
          <Title>
            {grant.url ? <a href={grant.url}>{grant.title}</a> : grant.title}
          </Title>
        </Col>
        <Col md="auto" className="mb-3">
          <DateRange>
            {grant.start_date} – {grant.end_date}
          </DateRange>
        </Col>
      </Row>

      <Row>
        <Col className="mb-3">
          <PIs>
            <PILabel>{grant.PIs.length > 1 ? "PIs: " : "PI: "}</PILabel>
            <Authors names={grant.PIs} people={people} />
            {grant.CoPIs && grant.CoPIs.length > 0 && (
              <>
                <PILabel>
                  {grant.CoPIs.length > 1 ? " Co-PIs: " : " Co-PI:"}
                </PILabel>
                <Authors names={grant.CoPIs} people={people} />
              </>
            )}
          </PIs>
        </Col>
      </Row>

      {grant.funding_agency_description && (
        <Row>
          <Col className="mb-3">
            <FundingAgencyDescription>
              {grant.funding_agency_description}
            </FundingAgencyDescription>
          </Col>
        </Row>
      )}
    </div>
  );
};

const FundingList = ({ funding, people }) => {
  const grants = funding["Grants"]
    .slice()
    .sort((a, b) => Date.parse(b.start_date) - Date.parse(a.start_date));

  return (
    <FundingContainer>
      <FundingContent>
        <StyledTitle className="mb-4">Funding</StyledTitle>

        <p className="mb-4">
          Our group is thankful to receive funding from many sources. Below, we
          list the size of the grants along with our portion of those grants in
          parenthesis.
        </p>

        <Stack direction="vertical" gap={4}>
          {grants.map((grant) => (
            <Grant grant={grant} people={people} key={grant.title} />
          ))}
        </Stack>
      </FundingContent>
    </FundingContainer>
  );
};

const copyToClipboard = async (text) => {
  await navigator.clipboard.writeText(text);
};

const AddEditFunding = ({ funding = { Grants: [] } }) => {
  const [outputJson, setOutputJson] = useState(null);
  const [agree, setAgree] = useState(false);
  const [mode, setMode] = useState("add"); // 'add' | 'edit'
  const [selectedKey, setSelectedKey] = useState(""); // `${title}||${start_date}`
  const [prefill, setPrefill] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const options = useMemo(() => {
    return (funding?.Grants || [])
      .map((g) => ({
        key: `${g.title}||${g.start_date || ''}`,
        label: `${g.title} — ${g.start_date || ''}`,
        grant: g,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [funding]);

  function switchMode(next) {
    setMode(next);
    if (next === "add") {
      setPrefill(null);
      setSelectedKey("");
      setFormKey((k) => k + 1);
    }
  }

  function handleSelectExisting(e) {
    const key = e.target.value;
    setSelectedKey(key);
    const found = options.find((o) => o.key === key);
    if (!found) {
      setPrefill(null);
      return;
    }
    const g = found.grant;
    const PIs = (g.PIs || []).join(", ");
    const CoPIs = (g.CoPIs || []).join(", ");
    setPrefill({
      start_date: g.start_date || "",
      end_date: g.end_date || "",
      title: g.title || "",
      funder: g.funder || "",
      amount: g.amount || "",
      amount_we_get: g.amount_we_get || "",
      funding_agency_description: g.funding_agency_description || "",
      PIs,
      CoPIs,
      url: g.url || "",
      previousTitle: g.title || "",
      previousStartDate: g.start_date || "",
    });
    setFormKey((k) => k + 1);
  }

  function handleSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const raw = Object.fromEntries(formData.entries());

    const PIs = (raw.PIs || "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const CoPIs = (raw.CoPIs || "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const grant = {
      start_date: raw.start_date || "",
      end_date: raw.end_date || "",
      title: raw.title || "",
      funder: raw.funder || "",
      amount: raw.amount || "",
      PIs,
    };
    if ((raw.amount_we_get || "").trim() !== "") grant.amount_we_get = raw.amount_we_get.trim();
    if ((raw.funding_agency_description || "").trim() !== "") grant.funding_agency_description = raw.funding_agency_description.trim();
    if (CoPIs.length > 0) grant.CoPIs = CoPIs;
    if ((raw.url || "").trim() !== "") grant.url = raw.url.trim();

    const payload =
      mode === "edit"
        ? {
            action: "update",
            previousTitle: prefill?.previousTitle || grant.title,
            previousStartDate: prefill?.previousStartDate || grant.start_date,
            ...grant,
          }
        : grant;
    setOutputJson(payload);
  }

  const jsonString = outputJson ? JSON.stringify(outputJson, null, 2) : "";

  return (
    <div>
      <h3>Add or edit a grant</h3>
      <Helmet>
        <title>Add or edit a grant - Khoury Vis Lab</title>
        <meta name="description" content="Add or edit a grant" />
      </Helmet>

      <div style={{ marginBottom: 12 }}>
        <label>
          <input
            type="radio"
            name="mode"
            value="add"
            checked={mode === "add"}
            onChange={() => switchMode("add")}
          />{' '}
          Add
        </label>{' '}
        <label style={{ marginLeft: 16 }}>
          <input
            type="radio"
            name="mode"
            value="edit"
            checked={mode === "edit"}
            onChange={() => switchMode("edit")}
          />{' '}
          Edit
        </label>
      </div>

      {mode === "edit" && (
        <div style={{ marginBottom: 12 }}>
          <label>
            <strong>Select existing grant</strong>
            <br />
            <select value={selectedKey} onChange={handleSelectExisting}>
              <option value="">-- choose --</option>
              {options.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      <form key={formKey} method="post" onSubmit={handleSubmit}>
        <hr />
        <h4>Required fields</h4>
        <p>
          <label>
            <strong>Start date</strong> — YYYY-MM or YYYY-MM-DD
            <br />
            <textarea name="start_date" rows={1} cols={120} defaultValue={prefill?.start_date || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>End date</strong> — YYYY-MM or YYYY-MM-DD
            <br />
            <textarea name="end_date" rows={1} cols={120} defaultValue={prefill?.end_date || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Title</strong>
            <br />
            <textarea name="title" rows={1} cols={120} defaultValue={prefill?.title || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Funder</strong>
            <br />
            <textarea name="funder" rows={1} cols={120} defaultValue={prefill?.funder || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Amount</strong>
            <br />
            <textarea name="amount" rows={1} cols={120} defaultValue={prefill?.amount || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>PIs</strong> — comma-separated names
            <br />
            <textarea name="PIs" rows={1} cols={120} defaultValue={prefill?.PIs || ''} />
          </label>
        </p>

        <h4>Optional fields</h4>
        <p>
          <label>
            <strong>Amount we get</strong>
            <br />
            <textarea name="amount_we_get" rows={1} cols={120} defaultValue={prefill?.amount_we_get || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>Funding agency description</strong>
            <br />
            <textarea name="funding_agency_description" rows={1} cols={120} defaultValue={prefill?.funding_agency_description || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>CoPIs</strong> — comma-separated names
            <br />
            <textarea name="CoPIs" rows={1} cols={120} defaultValue={prefill?.CoPIs || ''} />
          </label>
        </p>
        <p>
          <label>
            <strong>URL</strong>
            <br />
            <textarea name="url" rows={1} cols={120} defaultValue={prefill?.url || ''} />
          </label>
        </p>

        <hr />
        <h4>Submitting</h4>
        <label>
          <input
            type="checkbox"
            defaultChecked={false}
            checked={agree}
            onChange={() => setAgree(!agree)}
          />{' '}
          I have filled required fields and checked for accuracy.
        </label>
        <Button type="submit" className="mt-3 d-block" disabled={!agree}>
          Step 1: Generate JSON
        </Button>
      </form>

      {outputJson && (
        <div>
          <Stack direction="horizontal" gap={3} className="mt-2">
            <Button onClick={() => copyToClipboard(jsonString)}>
              Step 2: Copy to clipboard
            </Button>
            <Button
              className="mt-2"
              onClick={() => {
                const isEdit = mode === "edit";
                const baseUrl = isEdit
                  ? "https://github.com/KhouryVis/khouryvis.github.io/issues/new?assignees=codydunne%2C+makhoroz%2C+airyimbin&labels=update_funding&projects=&title=[UPDATE GRANT] "
                  : "https://github.com/KhouryVis/khouryvis.github.io/issues/new?assignees=codydunne%2C+makhoroz%2C+airyimbin&labels=new_funding&projects=&template=new-funding-template.md&title=[NEW GRANT] ";
                const title = encodeURIComponent(outputJson.title || "");
                const url = baseUrl + title;
                window.open(url);
              }}
            >
              Step 3: Paste into new issue
            </Button>
          </Stack>
          <pre>{jsonString}</pre>
        </div>
      )}
    </div>
  );
};

const Funding = ({ funding, people }) => {
  let { path } = useRouteMatch();
  return (
    <Switch>
      <Route exact path={path}>
        <FundingList funding={funding} people={people} />
      </Route>
      <Route path={`${path}/add`}>
        <AddEditFunding funding={funding} />
      </Route>
    </Switch>
  );
};

export default Funding;
